/**
 * Shares the signed-in session between this app's open tabs.
 *
 * The token lives in sessionStorage (see authStorage.ts), which a browser
 * gives to each tab separately — so without this, opening Settings in a new
 * tab from the right-click menu lands on the sign-in page even though the
 * user is signed in right next to it. A tab that starts with no token asks
 * the others over a BroadcastChannel, and any tab holding one answers.
 *
 * This keeps what the sessionStorage choice was for: nothing is written
 * anywhere that outlives the browser. The session lasts as long as SOME tab
 * holds it and is gone once the last one closes, rather than per tab.
 *
 * A BroadcastChannel only reaches pages of the same origin, which could read
 * sessionStorage directly anyway, so answering hands the token to nothing
 * that couldn't already take it.
 */

const CHANNEL_NAME = 'cp.auth';

/**
 * How long a new tab waits for an answer before showing sign-in. Open tabs
 * answer within a few milliseconds; the wait only runs out in full when no
 * other tab is signed in, which is exactly when a short one matters.
 */
export const TOKEN_REQUEST_TIMEOUT_MS = 300;

type AuthMessage =
  | { type: 'token-request' }
  | { type: 'token'; token: string }
  | { type: 'signed-out'; token: string };

function isAuthMessage(value: unknown): value is AuthMessage {
  if (typeof value !== 'object' || value === null) return false;
  const message = value as { type?: unknown; token?: unknown };
  if (message.type === 'token-request') return true;
  return (
    (message.type === 'token' || message.type === 'signed-out') && typeof message.token === 'string'
  );
}

function openChannel(): BroadcastChannel | null {
  if (typeof BroadcastChannel === 'undefined') return null;
  try {
    return new BroadcastChannel(CHANNEL_NAME);
  } catch {
    return null;
  }
}

/** False where the browser can't talk between tabs, so there is no one to ask. */
export function canShareSession(): boolean {
  return typeof BroadcastChannel !== 'undefined';
}

/**
 * Asks the other open tabs for their token. Resolves with the first one that
 * answers, or null when none does in time.
 */
export function requestTokenFromOtherTabs({
  signal,
  timeoutMs = TOKEN_REQUEST_TIMEOUT_MS,
}: { signal?: AbortSignal; timeoutMs?: number } = {}): Promise<string | null> {
  const channel = openChannel();
  if (!channel) return Promise.resolve(null);

  return new Promise((resolve) => {
    const finish = (token: string | null) => {
      window.clearTimeout(timer);
      signal?.removeEventListener('abort', abort);
      channel.close();
      resolve(token);
    };
    const abort = () => finish(null);

    const timer = window.setTimeout(() => finish(null), timeoutMs);
    signal?.addEventListener('abort', abort);

    channel.onmessage = (event: MessageEvent<unknown>) => {
      if (isAuthMessage(event.data) && event.data.type === 'token') finish(event.data.token);
    };
    channel.postMessage({ type: 'token-request' } satisfies AuthMessage);
  });
}

/**
 * Answers other tabs' requests with this tab's token, and hears about a sign
 * out in another tab. Returns the cleanup.
 */
export function listenToOtherTabs({
  getToken,
  onSignedOut,
}: {
  getToken: () => string | null;
  onSignedOut: (token: string) => void;
}): () => void {
  const channel = openChannel();
  if (!channel) return () => {};

  channel.onmessage = (event: MessageEvent<unknown>) => {
    if (!isAuthMessage(event.data)) return;

    if (event.data.type === 'token-request') {
      const token = getToken();
      if (token) channel.postMessage({ type: 'token', token } satisfies AuthMessage);
    } else if (event.data.type === 'signed-out') {
      onSignedOut(event.data.token);
    }
  };

  return () => channel.close();
}

/**
 * Tells the other tabs this token was signed out, so they drop it too rather
 * than carrying on until their next request fails. The token rides along so a
 * tab signed in separately, as someone else, is left alone.
 */
export function announceSignOut(token: string): void {
  const channel = openChannel();
  if (!channel) return;
  channel.postMessage({ type: 'signed-out', token } satisfies AuthMessage);
  channel.close();
}
